import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Card } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { skills } from '../data/mock';

const Skills = () => {
  const [activeTab, setActiveTab] = useState('backend');
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  const SkillBar = ({ skill, index }) => {
    const [width, setWidth] = useState(0);

    useEffect(() => {
      if (inView) {
        setTimeout(() => {
          setWidth(skill.level);
        }, index * 100);
      }
    }, [inView, skill.level, index]);

    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ delay: index * 0.1 }}
        className="space-y-2"
      >
        <div className="flex justify-between items-center">
          <span className="text-slate-200 font-medium">{skill.name}</span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.5 + index * 0.1 }}
            className="text-emerald-400 text-sm font-semibold"
          >
            {width}%
          </motion.span>
        </div>
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden relative">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: inView ? `${width}%` : 0 }}
            transition={{ duration: 1, delay: index * 0.1, ease: 'easeOut' }}
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full relative"
          >
            {/* Shine effect */}
            <motion.div
              animate={{
                x: ['-100%', '200%']
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3,
                ease: 'easeInOut'
              }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            />
          </motion.div>
        </div>
      </motion.div>
    );
  };

  return (
    <section id="skills" className="py-24 bg-slate-950 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-emerald-500/5 rounded-full filter blur-3xl"></div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Technical <span className="text-emerald-400">Skills</span>
          </h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 mx-auto rounded-full mb-6"
          />
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            A comprehensive toolkit built through years of hands-on experience in modern web and mobile development.
          </p>
        </motion.div>

        {/* Skills Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-12 bg-slate-800/50 border border-slate-700 p-1 rounded-lg backdrop-blur-sm">
              <TabsTrigger
                value="backend"
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white transition-all duration-300 relative"
              >
                Backend
              </TabsTrigger>
              <TabsTrigger
                value="frontend"
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white transition-all duration-300"
              >
                Frontend
              </TabsTrigger>
              <TabsTrigger
                value="tools"
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white transition-all duration-300"
              >
                Tools
              </TabsTrigger>
            </TabsList>

            <div ref={ref}>
              <TabsContent value="backend" className="mt-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="bg-slate-800/30 border-slate-700 p-8 backdrop-blur-sm">
                    <div className="grid md:grid-cols-2 gap-6">
                      {skills.backend.map((skill, index) => (
                        <SkillBar key={index} skill={skill} index={index} />
                      ))}
                    </div>
                  </Card>
                </motion.div>
              </TabsContent>

              <TabsContent value="frontend" className="mt-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="bg-slate-800/30 border-slate-700 p-8 backdrop-blur-sm">
                    <div className="grid md:grid-cols-2 gap-6">
                      {skills.frontend.map((skill, index) => (
                        <SkillBar key={index} skill={skill} index={index} />
                      ))}
                    </div>
                  </Card>
                </motion.div>
              </TabsContent>

              <TabsContent value="tools" className="mt-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="bg-slate-800/30 border-slate-700 p-8 backdrop-blur-sm">
                    <div className="grid md:grid-cols-2 gap-6">
                      {skills.tools.map((skill, index) => (
                        <SkillBar key={index} skill={skill} index={index} />
                      ))}
                    </div>
                  </Card>
                </motion.div>
              </TabsContent>
            </div>
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;